/* eslint-disable react-perf/jsx-no-new-function-as-prop */
'use client';

import type {FC} from 'react';
import {FaArrowRightLong} from 'react-icons/fa6';
import clsx from 'clsx';
import useEmblaCarousel from 'embla-carousel-react';
import Link from 'next/link';

import type {ContainerSpacing} from '@/modules/foundation/components/container';
import {Container} from '@/modules/foundation/components/container';
import {Heading} from '@/modules/foundation/components/typography/heading';
import {internalLinkToHref} from '@/modules/foundation/sanity/link/query';
import type {KeyedArray} from '@/modules/foundation/shared/types';

import type {ArticleDetails} from '../../shared/types';
import {ArticleCard} from '../article-card';

import {useDotButton} from './useDotButton';

interface Props {
	title?: string;
	spacingTop?: ContainerSpacing;
	spacingBottom?: ContainerSpacing;
	articles: KeyedArray<ArticleDetails>;
	showLink?: boolean;
}

const ArticleList: FC<Props> = ({title, spacingBottom, spacingTop, articles, showLink = true}) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({skipSnaps: true, align: 'start'});
	const {selectedIndex, scrollSnaps, onDotButtonClick} = useDotButton(emblaApi);

	return (
		<Container
			spacingBottom={spacingBottom}
			spacingTop={spacingTop}
			width="screen"
			className={clsx('bg-beige-100')}
		>
			<div className={clsx('px-4', 'overflow-hidden')}>
				<div className={clsx('max-w-screen-xl', 'max-md:mx-auto')}>
					<div className={clsx('py-8', 'md:py-10')}>
						{title && (
							<div className={clsx('mb-4', 'md:mb-6')}>
								<Heading level={2} size="h3" spacing>
									{title}
								</Heading>
							</div>
						)}

						<div ref={emblaRef}>
							<ul
								className={clsx(
									'flex',
									'gap-4',
									'touch-pan-y',
									'touch-pinch-zoom',
									'md:grid',
									'md:grid-cols-3',
									'md:gap-8',
								)}
							>
								{articles && articles.length > 0 && (
									<>
										{articles.map((article) => {
											return (
												<li
													key={article._key}
													className={clsx(
														'basis-[90%]',
														'grow-0',
														'shrink-0',
													)}
												>
													<ArticleCard
														article={article}
														headingLevel={title ? 3 : 2}
													/>
												</li>
											);
										})}
									</>
								)}
							</ul>
						</div>
						{scrollSnaps.length > 1 && (
							<div
								className={clsx(
									'flex',
									'flex-wrap',
									'justify-center',
									'items-center',
									'py-4',
									'gap-3',
								)}
							>
								{scrollSnaps.map((_, index) => (
									<button
										type="button"
										key={index}
										onClick={() => onDotButtonClick(index)}
										aria-label={`Gå til artikkel ${index + 1}`}
										className={clsx(
											'relative',
											'inline-block',
											index === selectedIndex
												? 'bg-primary'
												: 'bg-[#26181726]/15',
											'w-8',
											'h-1',
											'cursor-pointer',
											'rounded-[1px]',
										)}
									/>
								))}
							</div>
						)}
						{showLink && (
							<div className={clsx('flex', 'justify-center')}>
								<Link
									href={internalLinkToHref('articleFrontpage')}
									className={clsx(
										'mt-5',
										'md:mt-8',
										'inline-flex',
										'text-center',
										'justify-center',
										'items-center',
										'gap-2',
										'font-bold',
										'text-base',
										'transition-colors',
										'duration-200',
										'group',
										'hover:text-red-600',
									)}
								>
									Se alle tips og inspirasjon
									<div
										className={clsx(
											'transition-all',
											'duration-200',
											'group-hover:pl-2',
										)}
									>
										<FaArrowRightLong aria-hidden />
									</div>
								</Link>
							</div>
						)}
					</div>
				</div>
			</div>
		</Container>
	);
};
export default ArticleList;
